import { DecisionContext, defaultDocumentLanguage, DocumentItem } from "../common/commonDomain";
import { autorun, makeAutoObservable, runInAction } from "mobx";
import { CompanyRole } from "../../types/CompanyInfo";
import { getFullName, sortedBoard, validateName } from "../../utils/Roles";
import { newUuid } from "../../utils/uuid";
import { BoardMember } from "../../types/BoardMember";
import { isEqual, sortBy, uniq } from "lodash";
import { Language } from "../../i18n/languages";
import { TFunction, TMessage } from "../../i18n/tt";
import { LocalizedText, SelectableWithId } from "../../common/common";
import { DecisionType } from "../../common/decisions";
import { DocumentType } from "../../common/documents";
import {
  BooleanFieldModel,
  LocalizedStringFieldModel,
  SelectableWithIdModel,
  StringFieldModel,
} from "../../common/commonForms";

export type AssignedProcuraByName = {
  id: string; // uuid
  boardMembers: BoardMember[];
  others: string[];
  isAlone: boolean;
  isSelected: boolean;
};

export type Procura = {
  type: DecisionType.procura;

  isProcuraAssignedByRole?: boolean;
  isProcuraAssignedByName?: boolean;
  isProcuraAssignedByFreetext?: boolean;

  procuraAssignedToByRole: Array<string>;
  procuraAssignedToByName: Array<AssignedProcuraByName>;
  revokeBoardDecisionsProcura?: boolean;
  freetexts: LocalizedText[];
};

export function isLanguageChanged(
  documentLanguages: Language[],
  localizedTexts: LocalizedStringFieldModel[],
): boolean {
  return !isEqual(sortBy(documentLanguages), sortBy(localizedTexts.map((t) => t.locale)));
}

export function updateLocalizedTexts(
  documentLanguages: Language[],
  localizedTexts: LocalizedStringFieldModel[],
): LocalizedStringFieldModel[] {
  return documentLanguages.map(
    (language) =>
      localizedTexts.find((t) => t.locale === language) ??
      new LocalizedStringFieldModel(
        new StringFieldModel({
          required: true,
          value: localizedTexts.length > 0 ? localizedTexts[0]?.model.inputValue : undefined,
        }),
        language,
      ),
  );
}

export const ceo = [
  {
    id: "ceoAlone",
    label: "Daglig leder alene",
    notCombinedWith: [
      "ceoOrChairpersonSeparate",
      "ceoOrMembersSeparate",
      "ceoAndChairpersonTogether",
      "ceoAndMembersTogether",
    ],
  },
  {
    id: "ceoOrChairpersonSeparate",
    label: "Daglig leder eller styreleder hver for seg",
    notCombinedWith: ["ceoAlone", "ceoAndChairpersonTogether", "chairpersonAlone"],
  },
  {
    id: "ceoOrMembersSeparate",
    label: "Daglig leder eller styrets medlemmer hver for seg",
    notCombinedWith: ["ceoAlone", "memberSeparate", "membersTogether"],
  },
  {
    id: "ceoAndChairpersonTogether",
    label: "Daglig leder og styreleder i felleskap",
    notCombinedWith: ["ceoAlone", "ceoOrChairpersonSeparate", "chairpersonAlone"],
  },
  {
    id: "ceoAndMembersTogether",
    label: "Daglig leder og styrets medlemmer i felleskap",
    notCombinedWith: ["ceoAlone"],
  },
];

export const chairperson = [
  {
    id: "chairpersonAlone",
    label: "Styreleder alene",
    notCombinedWith: [
      "ceoOrChairpersonSeparate",
      "ceoAndChairpersonTogether",
      "chairpersonOrDeputySeparate",
      "chairpersonAndDeputyTogether",
      "chairpersonAloneOrTwoMembersTogether",
      "chairpersonAndMemberTogether",
      "chairpersonAndTwoMembersTogether",
      "membersTogether",
    ],
  },
  {
    id: "chairpersonOrDeputySeparate",
    label: "Styreleder eller nestleder hver for seg",
    notCombinedWith: [
      "chairpersonAlone",
      "chairpersonAndDeputyTogether",
      "memberSeparate",
      "membersTogether",
    ],
  },
  {
    id: "chairpersonAndDeputyTogether",
    label: "Styreleder og nestleder i fellesskap",
    notCombinedWith: [
      "chairpersonAlone",
      "chairpersonOrDeputySeparate",
      "memberSeparate",
      "membersTogether",
    ],
  },
  {
    id: "chairpersonAloneOrTwoMembersTogether",
    label: "Styreleder alene eller to styremedlemmer i fellesskap",
    notCombinedWith: [
      "chairpersonAlone",
      "memberSeparate",
      "twoMembersTogether",
      "membersTogether",
    ],
  },
  {
    id: "chairpersonAndMemberTogether",
    label: "Styreleder og ett styremedlem i felleskap",
    notCombinedWith: [
      "chairpersonAlone",
      "chairpersonAndTwoMembersTogether",
      "memberSeparate",
      "membersTogether",
    ],
  },
  {
    id: "chairpersonAndTwoMembersTogether",
    label: "Styreleder og to styremedlemmer i felleskap",
    notCombinedWith: [
      "chairpersonAlone",
      "chairpersonAndMemberTogether",
      "memberSeparate",
      "membersTogether",
    ],
  },
];

export const boardMember = [
  {
    id: "memberSeparate",
    label: "Styrets medlemmer hver for seg",
    notCombinedWith: [
      "ceoOrMembersSeparate",
      "chairpersonOrDeputySeparate",
      "chairpersonAndDeputyTogether",
      "chairpersonAloneOrTwoMembersTogether",
      "chairpersonAndMemberTogether",
      "chairpersonAndTwoMembersTogether",
      "twoMembersTogether",
      "threeMembersTogether",
      "membersTogether",
    ],
  },
  {
    id: "twoMembersTogether",
    label: "To styremedlemmer i felleskap",
    notCombinedWith: [
      "chairpersonAloneOrTwoMembersTogether",
      "memberSeparate",
      "threeMembersTogether",
      "membersTogether",
    ],
  },
  {
    id: "threeMembersTogether",
    label: "Tre styremedlemmer i felleskap",
    notCombinedWith: ["memberSeparate", "twoMembersTogether", "membersTogether"],
  },
  {
    id: "membersTogether",
    label: "Styret i fellesskap",
    notCombinedWith: [
      "ceoOrMembersSeparate",
      "chairpersonAlone",
      "chairpersonOrDeputySeparate",
      "chairpersonAndDeputyTogether",
      "chairpersonAloneOrTwoMembersTogether",
      "chairpersonAndMemberTogether",
      "chairpersonAndTwoMembersTogether",
      "memberSeparate",
      "twoMembersTogether",
      "threeMembersTogether",
    ],
  },
];

export class ProcuraForm {
  readonly type = DecisionType.procura;
  readonly context: DecisionContext;

  isProcuraAssignedByRole: BooleanFieldModel;
  isProcuraAssignedByName: BooleanFieldModel;
  isProcuraAssignedByFreetext: BooleanFieldModel;

  procuraAssignedToByRole: SelectableWithIdModel;
  procuraAssignedToByName: ProcuraByNameModel;
  freetexts: LocalizedStringFieldModel[];
  revokeBoardDecisionsProcura: BooleanFieldModel;

  // fields for dialog when assigning procura by name
  showProcuraByNameDialog: boolean;

  procuraByName: NewProcuraByNameModel;

  constructor(
    context: DecisionContext,
    public data?: Procura,
  ) {
    this.context = context;

    this.isProcuraAssignedByRole = new BooleanFieldModel({
      value: data?.isProcuraAssignedByRole || false,
    });
    this.isProcuraAssignedByName = new BooleanFieldModel({
      value: data?.isProcuraAssignedByName || false,
    });
    this.isProcuraAssignedByFreetext = new BooleanFieldModel({
      value: data?.isProcuraAssignedByFreetext || false,
    });

    const selectableCeo = ceo.map((c) => ({ id: c.id, name: c.label, isSelected: false }));
    const selectableChairperson = chairperson.map((c) => ({
      id: c.id,
      name: c.label,
      isSelected: false,
    }));
    const selectableBoardMember = boardMember.map((c) => ({
      id: c.id,
      name: c.label,
      isSelected: false,
    }));
    this.procuraAssignedToByRole = new SelectableWithIdModel({
      value: selectableCeo.concat(selectableChairperson).concat(selectableBoardMember),
    });
    data?.procuraAssignedToByRole?.map((id) => this.procuraAssignedToByRole.select(id));

    this.procuraAssignedToByName = new ProcuraByNameModel({
      value: data?.procuraAssignedToByName ?? [],
    });

    this.freetexts =
      data?.freetexts.map(
        (t) =>
          new LocalizedStringFieldModel(
            new StringFieldModel({
              value: t.text,
              required: true,
            }),
            t.language,
          ),
      ) ?? [];

    this.revokeBoardDecisionsProcura = new BooleanFieldModel({
      value: data?.revokeBoardDecisionsProcura,
      required: true,
    });
    this.showProcuraByNameDialog = false;
    this.procuraByName = new NewProcuraByNameModel(this.boardMembers);
    makeAutoObservable(this);
  }

  init() {
    return autorun(() => {
      if (isLanguageChanged(this.documentLanguages, this.freetexts)) {
        runInAction(() => {
          this.freetexts = updateLocalizedTexts(this.documentLanguages, this.freetexts);
        });
      }
    });
  }

  get requiredDocuments(): DocumentItem[] {
    return this.context.currentDocuments.filter(
      (document) => document.type === DocumentType.board_meeting,
    );
  }

  get documentLanguages(): Language[] {
    const languages = uniq(this.requiredDocuments.flatMap((document) => document.languages || []));
    return languages.length > 0 ? languages : defaultDocumentLanguage(this.context.orgNo);
  }

  mapRoleToBoardMember(role: CompanyRole): BoardMember {
    return {
      companyroleId: role.companyroleId,
      name: getFullName(role),
    };
  }

  get boardMembers(): BoardMember[] {
    return sortedBoard(this.roles).map(this.mapRoleToBoardMember);
  }

  get roles(): CompanyRole[] {
    return this.context.companyInfo.roles;
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isValid() {
    return (
      this.isProcuraAssignedByRole.isValid &&
      this.isProcuraAssignedByName.isValid &&
      this.isProcuraAssignedByFreetext.isValid &&
      this.procuraAssignedToByRole.isValid &&
      this.revokeBoardDecisionsProcura.isValid
    );
  }

  validate(): boolean {
    this.isProcuraAssignedByRole.validate();
    this.isProcuraAssignedByName.validate();
    this.isProcuraAssignedByFreetext.validate();
    this.procuraAssignedToByRole.validate();
    this.revokeBoardDecisionsProcura.validate();
    return this.isValid;
  }

  toggleIsProcuraAssignedByRole() {
    this.isProcuraAssignedByRole.setValue(!this.isProcuraAssignedByRole.value);
    if (this.isProcuraAssignedByRole.value) this.isProcuraAssignedByFreetext.setValue(false);
  }

  toggleIsProcuraAssignedByName() {
    this.isProcuraAssignedByName.setValue(!this.isProcuraAssignedByName.value);
  }

  toggleIsProcuraAssignedByFreetext() {
    this.isProcuraAssignedByFreetext.setValue(!this.isProcuraAssignedByFreetext.value);
    if (this.isProcuraAssignedByFreetext.value) this.isProcuraAssignedByRole.setValue(false);
  }

  displayProcuraByNameDialog() {
    this.showProcuraByNameDialog = true;
  }

  hideProcuraByNameDialog() {
    this.showProcuraByNameDialog = false;
  }

  addNewProcuraAssignedToByName() {
    this.procuraByName.validate();
    if (this.procuraByName.isValid) {
      this.procuraAssignedToByName.add(this.procuraByName.toAssignedProcuraByName());
      this.showProcuraByNameDialog = false;
      this.procuraByName = new NewProcuraByNameModel(this.boardMembers);
    }
  }

  toggleProcuraAssignedToByName(id: string, checked: boolean) {
    if (checked) {
      this.procuraAssignedToByName.select(id);
    } else {
      this.procuraAssignedToByName.unselect(id);
    }
  }
}

class NewProcuraByNameModel {
  isAlone: BooleanFieldModel;
  assignedToBoardMembers: SelectableWithIdModel;
  assignedTogetherByName: StringFieldModel[];
  assignedAloneByName: StringFieldModel;

  constructor(boardMembers: BoardMember[]) {
    this.isAlone = new BooleanFieldModel({ required: true });
    this.assignedToBoardMembers = new SelectableWithIdModel({
      value: boardMembers.map((r) => ({
        name: r.name,
        id: r.companyroleId.toString(),
        isSelected: false,
      })),
    });
    this.assignedTogetherByName = [];
    this.assignedAloneByName = new StringFieldModel({ required: true, validator: validateName });
    makeAutoObservable(this);
  }

  setIsAlone(isAlone: boolean) {
    this.isAlone.setValue(isAlone);
  }

  toggleAssignedToBoardMembers(id: string): void {
    this.assignedToBoardMembers.toggle(id);
  }

  addNewByName() {
    if (this.assignedTogetherByName.every((value) => value.validate())) {
      this.assignedTogetherByName.push(
        new StringFieldModel({ required: true, validator: validateName }),
      );
    }
  }

  get isAloneValid(): boolean {
    return (
      this.isAlone.isValid &&
      this.isAlone.value === true &&
      this.assignedAloneByName.isValid &&
      this.assignedAloneByName.validatedValue !== ""
    );
  }

  get isTogetherValid(): boolean {
    return (
      this.isAlone.isValid &&
      this.isAlone.value === false &&
      this.procuraByNameLength > 1 &&
      this.assignedTogetherByName.every((d) => d.isValid && d.validatedValue !== "")
    );
  }

  get procuraByNameLength(): number {
    return this.assignedToBoardMembers.selectedCount + this.assignedTogetherByName.length;
  }

  get isValid(): boolean {
    return this.isAloneValid || this.isTogetherValid;
  }

  validate() {
    this.isAlone.validate();
    this.assignedTogetherByName.every((value) => value.validate());
    this.assignedAloneByName.validate();
  }

  toBoardMember(member: SelectableWithId): BoardMember {
    return {
      companyroleId: Number(member.id),
      name: member.name,
    };
  }

  toOtherMembers(): string[] {
    return this.isAlone.value === true
      ? [this.assignedAloneByName.validatedValue]
      : this.assignedTogetherByName
          .filter((d) => d.validatedValue !== "")
          .map((value) => value.validatedValue);
  }

  toAssignedProcuraByName(): AssignedProcuraByName {
    return {
      others: this.toOtherMembers(),
      boardMembers: this.assignedToBoardMembers.value
        .filter((s) => s.isSelected)
        .map((member) => this.toBoardMember(member)),
      isAlone: this.isAlone.value ?? true,
      isSelected: true,
      id: newUuid(),
    };
  }
}

type ProcuraByNameModelParams = {
  value?: Array<AssignedProcuraByName>;
  required?: boolean;
  onChange?: () => void;
};

export class ProcuraByNameModel {
  readonly params: ProcuraByNameModelParams;

  value: Array<AssignedProcuraByName>;
  error?: TMessage = undefined;

  constructor(params: ProcuraByNameModelParams = {}) {
    makeAutoObservable(this, { params: false }, { autoBind: true });
    this.params = params;
    this.value =
      params.value?.map((e) => ({
        id: e.id,
        boardMembers: e.boardMembers,
        others: e.others,
        isSelected: true,
        isAlone: e?.isAlone !== undefined ? e.isAlone : true,
      })) ?? [];
  }

  get isRequired(): boolean {
    return Boolean(this.params.required);
  }

  get isValid(): boolean {
    return !this.error;
  }

  formatToString(item: AssignedProcuraByName, t: TFunction): string {
    if (item.isAlone) {
      return item.others[0] + t("decisions.procura.alone");
    } else {
      const str =
        item.boardMembers.map((member) => member.name).join(", ") +
        (item.boardMembers.length > 0 && item.others.length > 0 ? ", " : "") +
        item.others.join(", ");

      const lastComma = str.lastIndexOf(",");
      return (
        str.substring(0, lastComma) +
        t("decisions.procura.and") +
        str.substring(lastComma + 1) +
        t("decisions.procura.together")
      );
    }
  }

  validate(): boolean {
    if (this.params.required && this.value.length === 0) {
      this.error = { key: "required" };
    } else {
      this.error = undefined;
    }
    return this.isValid;
  }

  add(item: AssignedProcuraByName) {
    if (!this.getById(item.id)) {
      this.value.push(item);
      this.params.onChange?.();
    }
  }

  isSelected(id: string): boolean {
    return this.getById(id)?.isSelected ?? false;
  }

  select(id: string) {
    const item = this.getById(id);
    if (item) {
      item.isSelected = true;
      this.params.onChange?.();
    }
  }

  selectAll() {
    this.value.forEach((value) => (value.isSelected = true));
    this.params.onChange?.();
  }

  unSelectAll() {
    this.value.forEach((value) => (value.isSelected = false));
    this.params.onChange?.();
  }

  unselect(id: string) {
    const item = this.getById(id);
    if (item) {
      item.isSelected = false;
      this.params.onChange?.();
    }
  }

  toggle(id: string) {
    if (this.isSelected(id)) {
      this.unselect(id);
    } else {
      this.select(id);
    }
  }

  getById(id: string): AssignedProcuraByName | undefined {
    return this.value.find((x) => x.id === id);
  }
}
